// FER local sign-in on this console (ADR 0016 decisions 19, 23, 29): a PIN plus the device secret FER created at
// enrolment. The device secret is shown once and kept only in this browser's storage for this console (never sent
// anywhere but `localSignIn`, never logged); the local token lives in memory only and lasts at most
// `local_session_hours`. A local session sees only the operations marked `x-local-auth`.
import type { LocalEnrolment, LocalToken } from "../api/types";
import type { Principal } from "../types";
import { FER_ROLES } from "./roles";

export interface StoredCredential {
  credential_id: string;
  device_secret: string;
  device_label: string;
  party_id: string;
  display_name: string | null;
  enrolled_at: string;
}

const PREFIX = "lcc.fer.local-credentials.v1:";

/** Storage key per API base (one console may serve one council only, but never mix councils' credentials). */
export function storageKey(apiBaseUrl: string): string {
  return `${PREFIX}${apiBaseUrl.replace(/\/+$/, "")}`;
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isStored(x: unknown): x is StoredCredential {
  const c = x as StoredCredential;
  return (
    !!c &&
    typeof c.credential_id === "string" &&
    typeof c.device_secret === "string" &&
    /^[A-Za-z0-9_-]{43}$/.test(c.device_secret) &&
    typeof c.device_label === "string" &&
    typeof c.party_id === "string"
  );
}

/** The local credentials enrolled on this console (unreadable or tampered entries are ignored). */
export function readStoredCredentials(apiBaseUrl: string): StoredCredential[] {
  const s = storage();
  if (!s) return [];
  try {
    const raw = s.getItem(storageKey(apiBaseUrl));
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(isStored) : [];
  } catch {
    return [];
  }
}

function write(apiBaseUrl: string, list: StoredCredential[]): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(storageKey(apiBaseUrl), JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/** Keep a new enrolment's device secret on this console. Answers false when the browser refuses storage. */
export function storeEnrolment(apiBaseUrl: string, e: LocalEnrolment, who: Pick<Principal, "partyId" | "displayName">): boolean {
  const list = readStoredCredentials(apiBaseUrl).filter((c) => c.credential_id !== e.credential_id);
  list.push({
    credential_id: e.credential_id,
    device_secret: e.device_secret,
    device_label: e.device_label,
    party_id: who.partyId,
    display_name: who.displayName ?? null,
    enrolled_at: e.created_at,
  });
  return write(apiBaseUrl, list);
}

/** Forget a credential on this console (removed, revoked or replaced). */
export function forgetStoredCredential(apiBaseUrl: string, credentialId: string): void {
  write(
    apiBaseUrl,
    readStoredCredentials(apiBaseUrl).filter((c) => c.credential_id !== credentialId),
  );
}

/** The contract's weak-PIN rule (422 `pin_too_weak`): all one digit, or a straight run up or down. */
export function weakPin(pin: string): boolean {
  if (!/^[0-9]{6,12}$/.test(pin)) return false;
  if (new Set(pin).size === 1) return true;
  const steps = new Set<number>();
  for (let i = 1; i < pin.length; i++) steps.add((Number(pin[i]) - Number(pin[i - 1]) + 10) % 10);
  return steps.size === 1 && (steps.has(1) || steps.has(9));
}

/** The claims of a JWT, unverified (for display only: party id and name; the server verifies every request). */
export function tokenClaims(token: string): Record<string, unknown> {
  try {
    const part = token.split(".")[1] ?? "";
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const json = new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === "object" ? (claims as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export interface LocalSession {
  token: string;
  expiresAt: string;
  principal: Principal;
  credentialId: string;
}

/**
 * The session a local token gives: its FER roles only, its ward scope, and the party id and name from its claims
 * (`LocalToken` itself carries no party id: contract gap). Null when the token names no party.
 */
export function localSession(t: LocalToken, credentialId: string): LocalSession | null {
  const claims = tokenClaims(t.access_token);
  const partyId = typeof claims.party_id === "string" ? claims.party_id : typeof claims.sub === "string" ? claims.sub : null;
  if (!partyId) return null;
  return {
    token: t.access_token,
    expiresAt: t.expires_at,
    credentialId,
    principal: {
      roles: t.roles.filter((r) => FER_ROLES.includes(r)),
      wardIds: t.ward_ids,
      partyId,
      displayName: typeof claims.name === "string" ? claims.name : null,
      auth: "local_auth",
    },
  };
}
